// Today home dashboard (D-038: layout matched to the owner-authorized reference, in the Adyvance
// palette). Greeting, three KPI cards with their own period and a trend line, quick links to
// the Adyvance services and Money (they replaced the leaderboard card, owner 2026-10-21), orders by hour and this week
// against the same days last week. Every figure comes from GET …/analytics/kpis; nothing is sample
// data. AI actions stay pending until the assistant is activated.
import { useId, useState } from "react";
import { formatDateAD, formatMoney } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { BusyRegion, cx, Icon, Skeleton } from "@ady/ui";
import { AiButton } from "../shell/AiButton";
import { AppLink } from "../shell/AppLink";
import { useShell } from "../shell/context";
import { can, findItem, hrefFor, iconFor, type NavItem } from "../shell/nav";
import { useKpis, type Kpis } from "../orders/api";
import { sfApi, sfKeys, useSf } from "../modules/storefront/api";
import { useCan, useStoreIds } from "../orders/ui";
import { LoadError } from "../settings/SettingsPage";
import { TID } from "../testids";
import { Chart, Sparkline } from "../charts/Charts";

type Period = Kpis["period"];
type CardKind = "revenue" | "orders" | "average";
const PERIODS: Period[] = ["today", "7d", "30d"];

const cardClass = "rounded-card border border-card bg-surface shadow-card";
const panelClass = "rounded-panel border border-card bg-surface shadow-card";

function nptHour(now = new Date()): number {
  return Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone: "Asia/Kathmandu",
    }).format(now),
  );
}

function greetingKey(hour: number) {
  if (hour < 12) return "today.home.greeting.morning" as const;
  if (hour < 17) return "today.home.greeting.afternoon" as const;
  return "today.home.greeting.evening" as const;
}

// ---------------------------------------------------------------- header

function Greeting() {
  const { me } = useShell();
  const reports = useCan("reports.read");
  const name =
    (me.user.display_name ?? "").trim().split(/\s+/)[0] || me.user.email?.split("@")[0] || "";
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex flex-col">
        <h1 className="text-lg leading-7 font-bold text-fg-heading" data-content="user">
          {t(greetingKey(nptHour()), { name })}
        </h1>
        <p className="text-xs text-fg">
          {reports ? t("today.home.statsFor") : t("today.home.dateOnly")}{" "}
          <span className="ady-text-brand font-bold">{formatDateAD(new Date(), "medium")}</span>
        </p>
      </div>
      <AiButton look="root" />
    </header>
  );
}

// ---------------------------------------------------------------- KPI cards

function trendValues(k: Kpis, kind: CardKind): number[] {
  // Today: by hour up to now (revenue cumulative); 7/30 days: by day.
  if (k.period === "today") {
    const upTo = k.hourly.slice(0, nptHour() + 1);
    if (kind === "orders") return upTo.map((h) => h.orders);
    if (kind === "average") return upTo.map((h) => (h.orders ? h.sales_paisa / h.orders : 0));
    let run = 0;
    return upTo.map((h) => (run += h.sales_paisa));
  }
  if (kind === "orders") return k.series.map((d) => d.orders);
  if (kind === "average") return k.series.map((d) => (d.orders ? d.sales_paisa / d.orders : 0));
  return k.series.map((d) => d.sales_paisa);
}

function KpiCard({ kind }: { kind: CardKind }) {
  const { orgId, storeId } = useStoreIds();
  const allowed = useCan("reports.read");
  const [period, setPeriod] = useState<Period>("today");
  const q = useKpis(orgId, storeId, period, allowed);
  const selectId = useId();
  const title = t(`today.home.${kind}`);
  const k = q.data;
  const isMoney = kind !== "orders";
  const value = !k
    ? 0
    : kind === "revenue"
      ? k.sales_paisa
      : kind === "orders"
        ? k.orders
        : k.average_order_paisa;
  return (
    <section
      className={cx(cardClass, "flex min-h-42 flex-col px-4 pt-3.5 pb-3")}
      aria-label={title}
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-fg-heading">{title}</h2>
        <label htmlFor={selectId} className="sr-only">
          {t("today.home.periodLabel", { card: title })}
        </label>
        <span className="relative flex items-center">
          <select
            id={selectId}
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            className="min-h-7 appearance-none rounded-full bg-sunken py-0.5 ps-2.5 pe-6 text-2xs font-medium text-fg hover:bg-hover"
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {t(`commerce.today.periods.${p}`)}
              </option>
            ))}
          </select>
          <Icon
            name="chevronDown"
            size={12}
            className="pointer-events-none absolute end-2 text-fg-secondary"
          />
        </span>
      </header>
      <p className="pt-2 text-2xs text-fg-tertiary">
        {i18n.tDynamic(`today.home.subs.${kind}.${period}`, "today.home.revenue")}
      </p>
      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <BusyRegion busy={q.isPending} skeleton={<Skeleton className="mt-2 h-8 w-24" />}>
          <p className="ady-ledger flex items-baseline gap-1 pt-2">
            {isMoney ? (
              <span className="text-xs font-medium text-fg-tertiary">
                {t("today.home.currency")}
              </span>
            ) : null}
            <span className="text-title-md leading-8 font-semibold">
              {isMoney ? formatMoney(value, { bare: true }) : value.toLocaleString("en-IN")}
            </span>
          </p>
          {k ? (
            <div className="px-3 pt-4">
              <Sparkline
                values={trendValues(k, kind)}
                variant={kind === "revenue" ? "line" : kind === "orders" ? "dots" : "gradient"}
                label={t("today.home.trend", { card: title })}
              />
            </div>
          ) : null}
        </BusyRegion>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- quick links (owner, 2026-10-21)

/** Replaces the leaderboard card: shortcuts to the Adyvance services, Daraz and Earnings, in this order. */
const QUICK_LINKS = ["importMarketplace", "digitalMarketing", "daraz", "courses", "money"] as const;

function QuickLinks() {
  const { membership, orgSlug, storeSlug } = useShell();
  const items = QUICK_LINKS.map((id) => findItem(id)).filter(
    (i): i is NavItem => Boolean(i) && can(membership, i!.anyOf),
  );
  if (!items.length) return null;
  return (
    <nav aria-label={t("today.home.quickLinks")}>
      <ul className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {items.map((item) => (
          <li key={item.id}>
            <AppLink
              href={hrefFor(item, orgSlug, storeSlug)}
              className={cx(
                cardClass,
                "flex h-full flex-col gap-2 p-4 text-fg no-underline hover:bg-hover",
              )}
            >
              <span className="flex size-10 flex-none items-center justify-center rounded-xs border border-card bg-surface">
                <Icon name={iconFor(item)} gradient />
              </span>
              <span className="ady-hover-label text-sm font-semibold">
                {item.id === "money" ? t("nav.groups.money") : t(item.label)}
              </span>
              <span className="text-xs text-fg-secondary">
                {i18n.tDynamic(`today.home.quick.${item.id}`, "hubs.cardFallback")}
              </span>
            </AppLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function HourlyCard() {
  const { orgId, storeId } = useStoreIds();
  const allowed = useCan("reports.read");
  const q = useKpis(orgId, storeId, "today", allowed);
  const hours = q.data?.hourly ?? [];
  const labels = hours.map((h) =>
    t("today.home.hourly.hour", { hour: String(h.hour).padStart(2, "0") }),
  );
  return (
    <section
      className="flex flex-col rounded-sm border border-card bg-surface p-4"
      aria-labelledby="home-hourly-h"
    >
      <h2
        id="home-hourly-h"
        className="border-b border-subtle pb-4 text-md font-semibold text-fg-heading"
      >
        {t("today.home.hourly.title")}
      </h2>
      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <BusyRegion busy={q.isPending} skeleton={<Skeleton className="mt-4 h-64 w-full" />}>
          <div className="mt-auto pt-4">
            <Chart
              kind="line"
              groups={[hours.map((h) => h.orders)]}
              labels={labels}
              showLabel={(i) => i === labels.length - 1 || (i % 2 === 0 && i < labels.length - 2)}
              series={[t("today.home.hourly.axis")]}
              label={t("today.home.hourly.title")}
            />
            {hours.every((h) => h.orders === 0) ? (
              <p className="pt-2 text-center text-xs text-fg-tertiary">
                {t("today.home.hourly.empty")}
              </p>
            ) : null}
          </div>
        </BusyRegion>
      )}
    </section>
  );
}

const WEEKDAYS = Array.from({ length: 7 }, (_, i) =>
  new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(2026, 9, 18 + i)), // 18 Oct 2026 is a Sunday
  ),
);

function WeeklyCard() {
  const { orgId, storeId } = useStoreIds();
  const allowed = useCan("reports.read");
  const q = useKpis(orgId, storeId, "30d", allowed);
  const series = q.data?.series ?? [];
  // The last entry is today (NPT). This week runs Sunday → today; last week is the same weekdays.
  const today = series.length ? new Date(`${series[series.length - 1]!.date}T00:00:00Z`) : null;
  const dow = today ? today.getUTCDay() : 0;
  const thisWeek = series.slice(-(dow + 1)).map((d) => d.sales_paisa);
  const lastWeek = series.slice(-(dow + 8), -7).map((d) => d.sales_paisa);
  const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
  const now = sum(thisWeek);
  const before = sum(lastWeek);
  const pad = (a: number[]) => [...a, ...Array<number>(7 - a.length).fill(0)];
  const labels = WEEKDAYS.map((d, i) => (i === dow ? t("today.home.weekly.today") : d));
  return (
    <section
      className={cx(panelClass, "flex flex-col px-5 pt-4.5 pb-3")}
      aria-labelledby="home-weekly-h"
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id="home-weekly-h" className="text-md font-semibold text-fg-heading">
            {t("today.home.weekly.title")}
          </h2>
          <p className="text-xs text-fg-tertiary">{t("today.home.weekly.sub")}</p>
        </div>
        <ul className="flex gap-4 text-xs text-fg-secondary">
          <li className="flex items-center gap-1.5">
            <span className="ady-bg-brand size-2 rounded-xs" aria-hidden="true" />
            {t("today.home.weekly.thisWeek")}
          </li>
          <li className="flex items-center gap-1.5">
            <span className="size-2 rounded-xs bg-action-border" aria-hidden="true" />
            {t("today.home.weekly.lastWeek")}
          </li>
        </ul>
      </header>
      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <BusyRegion busy={q.isPending} skeleton={<Skeleton className="mt-4 h-64 w-full" />}>
          <p className="ady-ledger flex flex-wrap items-baseline gap-x-1 pt-4">
            <span className="text-xs font-medium text-fg-tertiary">{t("today.home.currency")}</span>
            <span className="text-title-md leading-8 font-semibold">
              {formatMoney(now, { bare: true })}
            </span>
            <span className="ps-2 text-xs text-fg-tertiary">
              {before === 0
                ? t("today.home.weekly.noLastWeek")
                : t("today.home.weekly.change", {
                    sign: now >= before ? "+" : "−",
                    pct: String(Math.round((Math.abs(now - before) / before) * 100)),
                  })}
            </span>
          </p>
          <div className="mt-auto pt-3">
            <Chart
              kind="bars"
              groups={[pad(thisWeek).map((p) => p / 100), pad(lastWeek).map((p) => p / 100)]}
              labels={labels}
              showLabel={() => true}
              series={[t("today.home.weekly.thisWeek"), t("today.home.weekly.lastWeek")]}
              label={t("today.home.weekly.title")}
            />
          </div>
        </BusyRegion>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- launch checklist (STORE-07)

/** Shown until every launch step is done: a new shop sees what still stops customers ordering. */
/**
 * What each setup step needs to be DONE (not only seen): the checklist shows a step only to someone
 * who can do it, and is hidden when none is left for them (owner, 2026-11-04: staff saw the owner's
 * setup steps).
 */
const SETUP_NEEDS: Record<string, string[]> = {
  products: ["products.write"],
  payment: ["settings.checkout"],
  delivery: ["shipping.settings"],
  legal: ["store.legal.update"],
  policies: ["store.legal.update"],
  theme: ["theme.edit"],
  live: ["store.publish"],
  first_order: ["orders.read"],
};

function SetupChecklist() {
  const { orgId, storeId, orgSlug, storeSlug } = useStoreIds();
  const { membership } = useShell();
  const allowed = useCan("store.read");
  const q = useSf(sfKeys.readiness(orgId, storeId), () => sfApi.readiness(orgId, storeId), allowed);
  if (!allowed || !q.data || q.data.done === q.data.total) return null;
  const mine = q.data.items.filter((i) => can(membership, SETUP_NEEDS[i.key] ?? ["org.update"]));
  // Nothing left that this person can do (only "first order", or only done steps): no checklist.
  if (!mine.some((i) => i.state !== "done" && i.key !== "first_order")) return null;
  const items = mine;
  const done = mine.filter((i) => i.state === "done").length;
  const total = mine.length;
  return (
    <section
      data-testid={TID.todaySetup}
      className={cx(cardClass, "px-4 pt-3.5 pb-3")}
      aria-labelledby="home-setup-h"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2">
        <div className="flex flex-col">
          <h2 id="home-setup-h" className="text-sm font-semibold text-fg-heading">
            {t("today.home.setup.title")}
          </h2>
          <p className="text-xs text-fg-secondary">{t("today.home.setup.lead")}</p>
        </div>
        <span className="text-xs text-fg-secondary">
          {t("storefront.admin.settings.readinessProgress", { done, total })}
        </span>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((i) => {
          const ok = i.state === "done";
          const label = t(`storefront.admin.settings.readiness.${i.key as "legal"}`);
          return (
            <li key={i.key} data-readiness={i.key} data-state={i.state}>
              {ok ? (
                <span className="flex min-h-control items-center gap-2 rounded-sm bg-sunken px-3 text-sm text-fg-secondary">
                  <Icon name="check" className="text-success" />
                  <span className="line-through">{label}</span>
                </span>
              ) : (
                <AppLink
                  href={`/${orgSlug}/${storeSlug}/${i.fix}`}
                  className="flex min-h-control items-center gap-2 rounded-sm bg-selected px-3 text-sm text-fg no-underline hover:bg-hover"
                >
                  <Icon name="circle" className="text-fg-secondary" />
                  <span className="flex-1">{label}</span>
                </AppLink>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------- queue (Adyvance addition)

function NeedsYou() {
  const { orgId, storeId, orgSlug, storeSlug } = useStoreIds();
  // The counts come from the KPI route, which needs reports.read (analytics.kpis). Asking with
  // orders.read only got a 403 that the shared KPI cache then showed on every card (2026-11-04).
  const canOrders = useCan("orders.read");
  const canReports = useCan("reports.read");
  const allowed = canOrders && canReports;
  const q = useKpis(orgId, storeId, "today", allowed);
  if (!allowed || !q.data) return null;
  const rows = [
    ["to-confirm", "commerce.today.toConfirm", q.data.queue.to_confirm],
    ["awaiting-verification", "commerce.today.toCheck", q.data.queue.awaiting_verification],
    ["unfulfilled-48h", "commerce.today.late", q.data.queue.unfulfilled_late],
    ["unconfirmed-cod", "commerce.today.unconfirmedCod", q.data.queue.unconfirmed_cod],
  ] as const;
  return (
    <section
      data-testid={TID.todayNeedsYou}
      className={cx(cardClass, "px-4 pt-3.5 pb-3")}
      aria-labelledby="home-queue-h"
    >
      <h2 id="home-queue-h" className="pb-2 text-sm font-semibold text-fg-heading">
        {t("today.home.queueTitle")}
      </h2>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map(([view, key, count]) => (
          <li key={view}>
            <AppLink
              href={`/${orgSlug}/${storeSlug}/orders?view=${view}`}
              className={cx(
                "flex min-h-11 items-center justify-between gap-2 rounded-sm px-3 text-sm no-underline hover:bg-hover",
                count > 0 ? "bg-selected text-fg" : "bg-sunken text-fg-secondary",
              )}
            >
              <span>{t(key)}</span>
              <span className="ady-ledger font-semibold">{count}</span>
            </AppLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function HomeDashboard() {
  // Home shows only what the role can see or do (owner, 2026-11-04): without Reports there are no
  // sales cards or charts at all (a staff invitee saw "Couldn't load this" five times).
  const reports = useCan("reports.read");
  return (
    <div className="flex flex-col gap-4">
      <Greeting />
      <SetupChecklist />
      {reports ? (
        <div className="grid gap-4 md:grid-cols-3">
          <KpiCard kind="revenue" />
          <KpiCard kind="orders" />
          <KpiCard kind="average" />
        </div>
      ) : null}
      <QuickLinks />
      {reports ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <HourlyCard />
          <WeeklyCard />
        </div>
      ) : null}
      <NeedsYou />
    </div>
  );
}
